/**
 * Making a clean recording sound like it came over the air.
 *
 * Deshret renders NPC lines with Fish Audio, which produces studio-clean speech. Dropped straight
 * onto the band that is *worse* than wrong — it is uncanny. Everything else the player hears is
 * thin, filtered and squashed, so a clean voice does not sound like a person who is really there;
 * it sounds like a voice-over playing on top of a game. The processing below is what makes an
 * NPC sound like they are on the other end of a radio rather than in the room.
 *
 * ## The chain, and why each stage is there
 *
 *   speech ─► high-pass 300 ─► low-pass 3000 ─► presence peak ─► compressor ─► soft clip ─► out
 *
 * - **300 Hz / 3 kHz.** The communications band. Real SSB voice gear is roughly this, and it is
 *   not an aesthetic choice so much as a legibility one: stripping the chest tones is most of
 *   what makes a voice read as "radio", and stripping the top is what stops it competing with the
 *   static, which lives above 4 kHz.
 * - **A peak at 1.9 kHz.** Consonants live here. The band-limiting above costs intelligibility,
 *   and this buys it back — the player has to be able to make out what is said, and Chapter 7's
 *   whole point is that atmosphere must not come at the cost of understanding.
 * - **Compression.** Radio gear rides its own gain hard, so quiet syllables come up and loud ones
 *   do not spike. It also means one authored clip cannot be much louder than another, however
 *   they were rendered.
 * - **Soft clip.** A gentle saturation, not distortion. It adds the small amount of grit that
 *   sells a transmitter without making words harder to catch.
 *
 * ## Clarity is a setting
 *
 * Chapter 7 also has to be accessible, and "authentically hard to hear" is a problem for anyone
 * who finds speech hard to follow. `setClarity` opens the passband out and eases the saturation,
 * so a player can keep the character of the effect while making the words plainer. At clarity 1
 * this chain is close to transparent. The story never depends on catching a line by ear — every
 * one is captioned — but a player should not have to read if they would rather listen.
 */
import { audioContext, rampTo } from './context.js';

/** Passband at full effect (clarity 0) and at full clarity (clarity 1). */
const HIGH_PASS_HZ = [300, 150] as const;
const LOW_PASS_HZ = [3000, 7000] as const;
/** Saturation drive at each end. 1 is linear. */
const DRIVE = [2.6, 1.15] as const;

const RAMP_SECONDS = 0.15;

/**
 * A soft-clip curve: `tanh(x·drive) / drive`.
 *
 * Dividing by `drive` rather than by `tanh(drive)` is the whole difference between saturation and
 * an expander. This form has **unity slope at zero**, so quiet speech passes through untouched and
 * only peaks are rounded off — which is what "gentle saturation" means. Normalising to the rails
 * instead would lift everything below full scale, and since a compressor already sits in front of
 * this stage, that would be levelling the signal twice and smearing every syllable.
 *
 * The cost is that peaks come out at `tanh(drive)/drive` of full scale, so the makeup gain has to
 * put that back — see `peakCompensation`.
 *
 * The table is generated once per drive setting: a WaveShaper's curve cannot be automated, so
 * changing clarity replaces it rather than ramping it. That is fine because clarity moves when a
 * player drags a slider, not while a word is playing.
 */
function softClipCurve(drive: number, samples = 1024): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(new ArrayBuffer(samples * 4));
  for (let i = 0; i < samples; i += 1) {
    const x = (i / (samples - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * drive) / drive;
  }
  return curve;
}

/** How much level the soft clip takes off the peaks, as a gain to put back. */
export function peakCompensation(drive: number): number {
  return drive / Math.tanh(drive);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export interface RadioVoice {
  /** Connect speech here. */
  readonly input: AudioNode;
  /** Connect this to a mixer bus. */
  readonly output: AudioNode;
  /**
   * 0 = full radio character, 1 = as close to the clean recording as this chain gets.
   * Ramped, so a player dragging the slider hears no steps.
   */
  setClarity(value: number): void;
  dispose(): void;
}

/**
 * Build one voice chain.
 *
 * One instance is shared by every clip rather than built per line: six nodes that exist for the
 * session cost nothing, and rebuilding the chain per utterance would both churn the graph and
 * reset the compressor's envelope at the start of every sentence.
 */
export function createRadioVoice(clarity = 0): RadioVoice {
  const ctx = audioContext();

  const highPass = ctx.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.Q.value = 0.7;

  const lowPass = ctx.createBiquadFilter();
  lowPass.type = 'lowpass';
  lowPass.Q.value = 0.7;

  const presence = ctx.createBiquadFilter();
  presence.type = 'peaking';
  presence.frequency.value = 1900;
  presence.Q.value = 1.1;
  presence.gain.value = 5;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -26;
  compressor.knee.value = 8;
  compressor.ratio.value = 5;
  // Fast enough to catch a plosive, slow enough not to chew the front off a word.
  compressor.attack.value = 0.006;
  compressor.release.value = 0.14;

  const shaper = ctx.createWaveShaper();
  shaper.oversample = '2x';

  // Makeup gain: band-limiting and compression both cost level, and a line that arrives quieter
  // than the static it is competing with is a legibility failure however good the processing is.
  const makeup = ctx.createGain();
  makeup.gain.value = 1.6;

  highPass.connect(lowPass).connect(presence).connect(compressor).connect(shaper).connect(makeup);

  let currentDrive = -1;

  const setClarity = (value: number): void => {
    const t = Math.min(1, Math.max(0, value));
    rampTo(highPass.frequency, lerp(HIGH_PASS_HZ[0], HIGH_PASS_HZ[1], t), RAMP_SECONDS);
    rampTo(lowPass.frequency, lerp(LOW_PASS_HZ[0], LOW_PASS_HZ[1], t), RAMP_SECONDS);
    // Less presence lift is needed once the passband is open.
    rampTo(presence.gain, lerp(5, 2, t), RAMP_SECONDS);
    rampTo(compressor.ratio, lerp(5, 2.5, t), RAMP_SECONDS);

    const drive = lerp(DRIVE[0], DRIVE[1], t);
    // Makeup covers two things: the level the band-limiting and compression cost, and the peak
    // the soft clip took off. Without the second term, turning clarity up would also turn the
    // voice up, and a clarity control that changes loudness is not a clarity control.
    rampTo(makeup.gain, lerp(1.6, 1.15, t) * peakCompensation(drive), RAMP_SECONDS);

    // Rebuilding the curve is the expensive part, so only when it has meaningfully moved.
    if (Math.abs(drive - currentDrive) > 0.02) {
      currentDrive = drive;
      shaper.curve = softClipCurve(drive);
    }
  };

  setClarity(clarity);

  return {
    input: highPass,
    output: makeup,
    setClarity,
    dispose: () => {
      highPass.disconnect();
      lowPass.disconnect();
      presence.disconnect();
      compressor.disconnect();
      shaper.disconnect();
      makeup.disconnect();
    },
  };
}

/**
 * The passband a given clarity produces, as a pure function.
 *
 * Exported so the curve can be tested without a Web Audio context, and so the settings UI can
 * describe what the slider is actually doing rather than showing a bare number.
 */
export function clarityPassband(clarity: number): { highPassHz: number; lowPassHz: number } {
  const t = Math.min(1, Math.max(0, clarity));
  return {
    highPassHz: lerp(HIGH_PASS_HZ[0], HIGH_PASS_HZ[1], t),
    lowPassHz: lerp(LOW_PASS_HZ[0], LOW_PASS_HZ[1], t),
  };
}

export { softClipCurve };
